/**
 * The pure rules of `ConversationCountsStore` (GLOBAL_CONVERSATIONS_SLICE2_SPEC.md §6.3, SD2-11, SD2-30, §4.6): which
 * project ids a `GetCounts` call carries, how an answer merges into the per-project counts, which answers (or which
 * parts of an answer: the badge, each project) are too old to apply, which dropped answers the store must reconcile
 * with its own call, the badge's optimistic arithmetic, the reconnect jitter and the 60 s circuit breaker.
 *
 * No Angular and no RxJS: the store wires these rules to its triggers, and `conversation-counts.rules.spec.ts` checks
 * them with plain Jasmine. The only imports are types, erased on compile.
 */
import type { IConversationTabCountsDto, IProjectConversationCountDto } from '@shared/service-proxies/service-proxies';

/**
 * The most project ids one `GetCounts` call may carry: C# `ConversationConsts.MaxCountProjects`. The server refuses
 * more with "Too many projects to count." (an ABP error modal), so the store never sends more.
 */
export const MAX_COUNT_PROJECTS = 500;

/** Pushes, watch events and `refresh()` are coalesced by `auditTime` over this window (§4.6; not `debounceTime`). */
export const COUNTS_AUDIT_MS = 750;

/**
 * A registration (or the remainder of a call that could not carry every id) is fetched once registrations have been
 * quiet this long, so ids registered in the same turn share one call.
 */
export const COUNTS_SETTLE_MS = 50;

/** After a failed call the store makes no background call for this long (SD2-30 circuit breaker). */
export const COUNTS_BREAKER_MS = 60000;

/** After a SignalR reconnect the store calls once, after a random delay from this ... */
export const RECONNECT_DELAY_MIN_MS = 3000;

/** ... to this (§4.6, R2-03). */
export const RECONNECT_DELAY_MAX_MS = 10000;

/** Who registers the ids it shows: the Projects list (visible rows ± 20), the open project page, the overlay. */
export type CountsOwner = 'project-list' | 'project-page' | 'overlay';

/**
 * Every owner, in the order their ids are sent when the union exceeds one call: the single-id owners first, so the
 * overlay's and the project page's project are never left for a later call.
 */
export const COUNTS_OWNERS: ReadonlyArray<CountsOwner> = ['overlay', 'project-page', 'project-list'];

/**
 * The counts of one project, as the chip and the project tab read them. A hand-written view of the answer, not the
 * generated `ProjectConversationCountDto`: `accessible` is false when a `GetCounts` that asked for the project
 * answered without it, or a project-scope feed said `ProjectAccessible = false`. A project the store has no answer
 * for yet has NO entry: unknown is not the same as not accessible.
 */
export interface ProjectConversationCounts {
    projectId: number;
    /** False: no chip and no tab for this project. */
    accessible: boolean;
    /** Every entry of the project (its tasks' comments and its project updates): the chip number, the grey dot. */
    entryCount: number;
    /** Entries needing the user's attention: the red dot. */
    unreadCount: number;
}

/** The per-project counts the store publishes on `projects$`, keyed by project id. */
export type ProjectCountsMap = ReadonlyMap<number, ProjectConversationCounts>;

/** Which answer is being ordered: a `GetCounts` answer, a feed answer (counts probe or page 1), a read-state answer. */
export type CountsAnswerKind = 'counts' | 'feed' | 'readState';

/**
 * What `answerVerdict` (one target) or `planAnswer` (a whole answer) decides:
 *  - `current`: apply it (`planAnswer`: the targets it lists);
 *  - `superseded`: its request was sent before a change its numbers cannot include (an optimistic change; for a
 *    counts or feed answer, also an applied read-state answer). Dropped, and reconciled by the store's own
 *    `GetCounts` (`droppedAnswerNeedsReconcile`);
 *  - `stale`: a newer answer was applied already (a later `AsOfUtc` for that target, or a read-state answer requested
 *    later), or there is no answer. Dropped; only a read-state answer is reconciled.
 */
export type CountsAnswerVerdict = 'current' | 'superseded' | 'stale';

/** What `answerVerdict` knows about an answer. */
export interface CountsAnswerStamp {
    kind: CountsAnswerKind;
    /** The answer's `AsOfUtc` (counts and feed answers); null for a read-state answer, which carries none. */
    asOfUtc: string | null;
    /**
     * The client request sequence taken when the request was sent (`ConversationCountsStore.nextRequestSeq`, a whole
     * number ≥ 1); null when it is not known (a caller that did not pass one), which skips the sequence rules.
     */
    requestSeq: number | null;
}

/**
 * The sequence part of what has been applied so far, the same for every target. Every sequence below comes from the
 * store's one client sequence (each request, each optimistic change and each applied read-state answer takes the next
 * number), so they compare with each other and never tie.
 */
export interface CountsSequenceState {
    /** The request sequence of the newest applied read-state answer that carried one; 0 before the first. */
    lastReadStateSeq: number;
    /** The sequence of the latest optimistic change; 0 before the first. */
    lastOptimisticSeq: number;
    /** The sequence at which the last read-state answer was applied; 0 before the first. */
    lastReadStateAppliedSeq: number;
}

/** What has been applied so far, for ONE target (the badge, or one project): the sequences plus that target's mark. */
export interface CountsOrderState extends CountsSequenceState {
    /**
     * The `AsOfUtc` (ms) of the last counts or feed answer applied to this target (the badge, or one project); null
     * before the first. Read-state answers carry no `AsOfUtc` and never move it.
     */
    lastAsOfMs: number | null;
}

/**
 * The `AsOfUtc` marks (ms) of what has been applied, kept per target (review fix): a page-1 probe and the store's
 * `GetCounts` fired by the same push may finish in either order, and one target's newer answer must not drop the other
 * targets of an older one.
 */
export interface CountsAsOfMarks {
    /** The last applied badge's (a `GetCounts` answer, an All-scope feed answer); null before the first. */
    badgeMs: number | null;
    /** Each project's last applied counts': a `GetCounts` row or requested-missing id, a project-scope feed answer. */
    projectMs: ReadonlyMap<number, number> | null;
}

/** What of one answer may be applied (`planAnswer`). */
export interface CountsAnswerPlan {
    /**
     * `superseded`: nothing applies (the sequence rules); `stale`: every target the answer carries already holds a
     * newer answer, so nothing applies and nothing is owed; `current`: `badge` and `projectIds` apply (an answer that
     * carries nothing is current and applies nothing).
     */
    verdict: CountsAnswerVerdict;
    /** Whether the answer's badge (`NavBadgeCount`) applies. */
    badge: boolean;
    /** The projects whose counts apply (rows, or requested ids the answer left out), in order. */
    projectIds: number[];
}

/** The tab counts a feed answer contributes to a project: Needs attention → unread, All → entries (INV-2, INV-3). */
export type FeedProjectCountsSource = Pick<IConversationTabCountsDto, 'attentionCount' | 'allCount'>;

const NO_PROJECT_COUNTS: ProjectCountsMap = new Map<number, ProjectConversationCounts>();

/** A valid project id: a positive whole number. */
export function isProjectId(value: unknown): value is number {
    return isPositiveInteger(value);
}

/** Whether `owner` is one of `COUNTS_OWNERS`. */
export function isCountsOwner(owner: unknown): owner is CountsOwner {
    return COUNTS_OWNERS.indexOf(owner as CountsOwner) >= 0;
}

/**
 * A client request sequence as a caller hands it back: a whole number ≥ 1 (`nextRequestSeq` never gives 0).
 * Anything else (omitted, null, 0, NaN, a fraction) is "not known": null.
 */
export function toRequestSeq(value: number | null | undefined): number | null {
    return isPositiveInteger(value) ? value : null;
}

/** An owner's ids as the store keeps them: valid project ids only, each once, in the order given. */
export function normalizeProjectIds(ids: readonly number[] | null | undefined): number[] {
    const seen = new Set<number>();
    const result: number[] = [];

    (ids || []).forEach((id) => {
        if (isProjectId(id) && !seen.has(id)) {
            seen.add(id);
            result.push(id);
        }
    });
    return result;
}

/**
 * Split `ids` into chunks of at most `size`, in order. `size` is capped at `MAX_COUNT_PROJECTS`; null (or anything
 * that is not a positive whole number) means `MAX_COUNT_PROJECTS`. One call sends the first chunk; the rest stay to
 * fetch and go in the next call.
 */
export function chunkIds(ids: readonly number[] | null | undefined, size: number | null): number[][] {
    const chunkSize = isPositiveInteger(size) && size <= MAX_COUNT_PROJECTS ? size : MAX_COUNT_PROJECTS;
    const all = (ids || []).slice();
    const chunks: number[][] = [];

    for (let start = 0; start < all.length; start += chunkSize) {
        chunks.push(all.slice(start, start + chunkSize));
    }
    return chunks;
}

/**
 * Every registered id, each once: the owners in `COUNTS_OWNERS` order, each owner's ids in its own order. An owner
 * that is not registered contributes nothing.
 */
export function unionRegistered(
    registrations: ReadonlyMap<CountsOwner, readonly number[]> | null | undefined
): number[] {
    const seen = new Set<number>();
    const result: number[] = [];

    COUNTS_OWNERS.forEach((owner) => {
        const ids = registrations ? registrations.get(owner) : null;

        (ids || []).forEach((id) => {
            if (isProjectId(id) && !seen.has(id)) {
                seen.add(id);
                result.push(id);
            }
        });
    });
    return result;
}

/**
 * The registered ids a call must fetch: those new to the store (never fetched) and those whose last fetch predates
 * the last refresh (`fetchedGen` < `currentGen`), so a stale id scrolled back into view is fetched again instead of
 * showing an old count indefinitely (SD2-11). Order kept.
 *
 * @param fetchedGen the refresh generation each id was last fetched in.
 * @param currentGen the store's refresh generation (bumped by every refresh: a push, a watch event, `refresh()`, a
 * reconnect, a restart).
 */
export function idsToFetch(
    registered: readonly number[] | null | undefined,
    fetchedGen: ReadonlyMap<number, number> | null | undefined,
    currentGen: number
): number[] {
    return (registered || []).filter((id) => {
        const gen = fetchedGen ? fetchedGen.get(id) : undefined;

        return isProjectId(id) && (gen === undefined || gen < currentGen);
    });
}

/** Whether two counts say the same thing (so an unchanged answer publishes nothing). */
export function sameProjectCounts(
    a: ProjectConversationCounts | null | undefined,
    b: ProjectConversationCounts | null | undefined
): boolean {
    if (!a || !b) {
        return !a && !b;
    }
    return (
        a.projectId === b.projectId &&
        a.accessible === b.accessible &&
        a.entryCount === b.entryCount &&
        a.unreadCount === b.unreadCount
    );
}

/** The counts of a project the caller cannot open (or that an answer left out): no chip, no tab. */
export function notAccessibleCounts(projectId: number): ProjectConversationCounts {
    return { projectId, accessible: false, entryCount: 0, unreadCount: 0 };
}

/**
 * Merge an answer's rows into `current`. Each row's project becomes accessible with its counts; every id in
 * `requestedIds` that the answer left out becomes not accessible (the answer holds every accessible requested project,
 * zeros included); every other id is left as it was ("merge keeps unknown ids"). Pass `requestedIds` = null when the
 * request is not known (an answer the caller hands over, a read-state answer): only its rows are merged.
 *
 * Returns `current` itself when nothing changed, else a new map (`current` is never mutated).
 */
export function mergeProjectCounts(
    current: ProjectCountsMap | null | undefined,
    rows: ReadonlyArray<IProjectConversationCountDto> | null | undefined,
    requestedIds: readonly number[] | null
): ProjectCountsMap {
    const answered = new Set<number>();
    const updates: ProjectConversationCounts[] = [];

    (rows || []).forEach((row) => {
        const projectId = row ? row.projectId : 0;

        if (isProjectId(projectId)) {
            answered.add(projectId);
            updates.push({
                projectId,
                accessible: true,
                entryCount: countOf(row.entryCount),
                unreadCount: countOf(row.unreadCount),
            });
        }
    });
    (requestedIds || []).forEach((projectId) => {
        if (isProjectId(projectId) && !answered.has(projectId)) {
            answered.add(projectId);
            updates.push(notAccessibleCounts(projectId));
        }
    });
    return withProjectCounts(current, updates);
}

/**
 * Every project an answer speaks about: its rows' ids, then the requested ids it left out (which `mergeProjectCounts`
 * marks not accessible); valid ids only, each once, in that order. `requestedIds` null: its rows only.
 */
export function answerProjectIds(
    rows: ReadonlyArray<IProjectConversationCountDto> | null | undefined,
    requestedIds: readonly number[] | null | undefined
): number[] {
    const rowIds = (rows || []).map((row) => (row && isProjectId(row.projectId) ? row.projectId : 0));

    return normalizeProjectIds(rowIds.concat(requestedIds || []));
}

/** Set one project's counts. Returns `current` itself when they are unchanged, else a new map. */
export function setProjectCounts(
    current: ProjectCountsMap | null | undefined,
    counts: ProjectConversationCounts
): ProjectCountsMap {
    return withProjectCounts(current, counts && isProjectId(counts.projectId) ? [counts] : []);
}

/**
 * A project's counts from a project-scope feed answer (§6.3): `AttentionCount` → `unreadCount`, `AllCount` →
 * `entryCount` (INV-2, INV-3); a project the feed refused (`ProjectAccessible = false`) is not accessible.
 */
export function feedProjectCounts(
    projectId: number,
    counts: FeedProjectCountsSource | null | undefined,
    accessible: boolean
): ProjectConversationCounts {
    if (!accessible) {
        return notAccessibleCounts(projectId);
    }
    return {
        projectId,
        accessible: true,
        entryCount: countOf(counts ? counts.allCount : 0),
        unreadCount: countOf(counts ? counts.attentionCount : 0),
    };
}

/** An `AsOfUtc` as epoch ms (`Date.parse`, never a string compare); null when missing or unparseable. */
export function parseAsOfMs(asOfUtc: string | null | undefined): number | null {
    const ms = typeof asOfUtc === 'string' && asOfUtc ? Date.parse(asOfUtc) : NaN;

    return isFinite(ms) ? ms : null;
}

/** The "last applied AsOfUtc" after applying an answer with `asOfUtc`: never moves back; unparseable leaves it. */
export function nextAsOfMark(lastAsOfMs: number | null, asOfUtc: string | null | undefined): number | null {
    const ms = parseAsOfMs(asOfUtc);

    if (ms === null) {
        return lastAsOfMs;
    }
    return lastAsOfMs === null || lastAsOfMs === undefined ? ms : Math.max(lastAsOfMs, ms);
}

/**
 * Whether an answer may still be applied to ONE target (the badge, or one project, whose mark `order.lastAsOfMs`
 * holds), and if not why (§6.3 answer ordering):
 *  - any answer whose request predates the latest optimistic change is `superseded` (its numbers do not include it);
 *  - a `GetCounts` or feed answer whose request predates the last applied read-state answer is `superseded` (it may
 *    have been counted before that write);
 *  - a `GetCounts` or feed answer whose `AsOfUtc` is older than the last one applied to this target is `stale` (equal
 *    is current; an unparseable or missing time never freezes the counts, so it is current);
 *  - a read-state answer, which carries no `AsOfUtc`, is `stale` when it was requested before the newest applied
 *    read-state answer (the client request sequence). Two read-state answers both requested after the last
 *    optimistic change are applied in request order.
 * A null `requestSeq` (not known) skips the sequence rules; no answer is `stale`.
 */
export function answerVerdict(
    answer: CountsAnswerStamp | null | undefined,
    order: CountsOrderState | null | undefined
): CountsAnswerVerdict {
    const asOfMs = answer ? parseAsOfMs(answer.asOfUtc) : null;
    const requestSeq = answer ? toRequestSeq(answer.requestSeq) : null;
    const lastAsOfMs = order && typeof order.lastAsOfMs === 'number' ? order.lastAsOfMs : null;

    if (!answer) {
        return 'stale';
    }
    if (!order) {
        return 'current';
    }
    if (requestSeq !== null && requestSeq < order.lastOptimisticSeq) {
        return 'superseded';
    }
    switch (answer.kind) {
        case 'counts':
        case 'feed':
            if (requestSeq !== null && requestSeq < order.lastReadStateAppliedSeq) {
                return 'superseded';
            }
            return asOfMs === null || lastAsOfMs === null || asOfMs >= lastAsOfMs ? 'current' : 'stale';
        case 'readState':
            return requestSeq === null || requestSeq >= order.lastReadStateSeq ? 'current' : 'stale';
        default:
            return unknownAnswerKind(answer.kind);
    }
}

/** Whether an answer may still be applied: `answerVerdict` says `current`. */
export function isAnswerCurrent(
    answer: CountsAnswerStamp | null | undefined,
    order: CountsOrderState | null | undefined
): boolean {
    return answerVerdict(answer, order) === 'current';
}

/**
 * What of a whole answer may be applied, target by target (§6.3; review fix). The sequence rules decide for the whole
 * answer (they are the same for every target); then the `AsOfUtc` rule decides for each target the answer carries,
 * against that target's own mark: the badge against the last applied badge, each project against that project's last
 * applied counts. So a `GetCounts` counted just before an All-scope page-1 probe drops only its badge and still
 * applies its project rows, and one counted just before a project-scope probe drops only that project. A read-state
 * answer carries no `AsOfUtc`, so all its targets apply once the sequence rules pass.
 *
 * @param carriesBadge whether the answer says anything about the badge (its `NavBadgeCount` is not undefined).
 * @param projectIds every project the answer speaks about (`answerProjectIds`).
 */
export function planAnswer(
    answer: CountsAnswerStamp | null | undefined,
    sequence: CountsSequenceState | null | undefined,
    marks: CountsAsOfMarks | null | undefined,
    carriesBadge: boolean,
    projectIds: readonly number[] | null | undefined
): CountsAnswerPlan {
    const ids = normalizeProjectIds(projectIds);
    const verdict = answerVerdict(answer, orderFor(sequence, null));
    let badge = false;
    let current: number[] = [];

    if (verdict !== 'current') {
        return { verdict, badge: false, projectIds: [] };
    }
    badge = carriesBadge === true && isAnswerCurrent(answer, orderFor(sequence, marks ? marks.badgeMs : null));
    current = ids.filter((projectId) => isAnswerCurrent(answer, orderFor(sequence, projectMark(marks, projectId))));
    if (!badge && current.length === 0 && (carriesBadge === true || ids.length > 0)) {
        return { verdict: 'stale', badge: false, projectIds: [] };
    }
    return { verdict: 'current', badge, projectIds: current };
}

/**
 * Whether an answer a caller handed over, once dropped, leaves something the store must reconcile with its own
 * `GetCounts` (review fix). This tab gets no push for its own read-state writes (SD2-10 echo rule, GC2-LIVE-02), so
 * nothing else would bring those counts back:
 *  - `superseded` (any kind): yes. Its numbers predate a change, but nothing newer covers its projects, and the badge
 *    it carried may never be answered again (a failed action only rolls its optimistic change back);
 *  - a `stale` read-state answer (requested before one already applied): yes. The two may have run in parallel, so
 *    the applied one can miss this one's write, and this one's projects may differ;
 *  - a `stale` counts or feed answer: no. Every target it carried holds a newer answer (`planAnswer`);
 *  - `current`: no, it was applied.
 */
export function droppedAnswerNeedsReconcile(kind: CountsAnswerKind, verdict: CountsAnswerVerdict): boolean {
    switch (verdict) {
        case 'current':
            return false;
        case 'superseded':
            return true;
        case 'stale':
            return kind === 'readState';
        default:
            return unknownVerdict(verdict);
    }
}

/**
 * The badge after an optimistic change of `delta` (Mark read −1, Mark unread +1, Undo +1; Mark all read: any delta
 * that reaches 0, e.g. `-Infinity`). Never below 0. An unknown badge (null: nothing answered yet) stays unknown: a
 * number is never made up. A delta of 0, NaN or +Infinity changes nothing.
 */
export function applyBadgeDelta(current: number | null, delta: number): number | null {
    const known = typeof current === 'number' && isFinite(current) ? current : null;
    const next = known !== null && typeof delta === 'number' ? known + delta : NaN;

    if (known === null || typeof delta !== 'number' || isNaN(delta) || delta === 0) {
        return known;
    }
    if (!isFinite(next)) {
        return next > 0 ? known : 0;
    }
    return Math.max(0, Math.round(next));
}

/**
 * The badge after an answer's `NavBadgeCount`: a number replaces it (never below 0); null means the server has no
 * attention epoch, so nothing is unread (hidden); undefined (the answer does not say) or a non-number keeps it.
 */
export function badgeFromAnswer(current: number | null, answered: number | null | undefined): number | null {
    if (answered === undefined) {
        return current;
    }
    if (answered === null) {
        return null;
    }
    if (typeof answered !== 'number' || !isFinite(answered)) {
        return current;
    }
    return Math.max(0, Math.floor(answered));
}

/**
 * Whether the circuit breaker is open: true for `COUNTS_BREAKER_MS` after a failed call (SD2-30), so a database
 * incident raises at most one ABP error modal a minute. Never failed (null), or a clock that went back: closed.
 */
export function breakerOpen(lastFailureAt: number | null, now: number): boolean {
    const elapsed = typeof lastFailureAt === 'number' && typeof now === 'number' ? now - lastFailureAt : NaN;

    return isFinite(elapsed) && elapsed >= 0 && elapsed < COUNTS_BREAKER_MS;
}

/**
 * The reconnect delay for a random number in [0, 1): 3000 to 10000 ms, whole ms (§4.6: spreads the platform-wide
 * reconnect burst and lands after the Azure group-rejoin window).
 */
export function reconnectDelayMs(random: number): number {
    const r = typeof random === 'number' && isFinite(random) ? Math.min(Math.max(random, 0), 1) : 0;
    const span = RECONNECT_DELAY_MAX_MS - RECONNECT_DELAY_MIN_MS + 1;

    return Math.min(RECONNECT_DELAY_MAX_MS, RECONNECT_DELAY_MIN_MS + Math.floor(r * span));
}

/**
 * Whether a `taskWatched` / `taskUnwatched` push is about the session user: the server sends one object with a
 * camelCase `userId` (TaskWatcherService). Anything else (another user, a list, nothing, no session user) is not.
 */
export function isWatchEventForUser(payload: unknown, userId: number | null | undefined): boolean {
    const candidate = payload as { userId?: unknown };

    if (!isPositiveInteger(userId) || typeof payload !== 'object' || payload === null || Array.isArray(payload)) {
        return false;
    }
    return candidate.userId === userId;
}

/** `base` with `updates` applied in order; `base` itself when none of them changes anything (never mutated). */
function withProjectCounts(
    base: ProjectCountsMap | null | undefined,
    updates: readonly ProjectConversationCounts[]
): ProjectCountsMap {
    const source: ProjectCountsMap = base || NO_PROJECT_COUNTS;
    let next: Map<number, ProjectConversationCounts> | null = null;

    for (const counts of updates) {
        if (!sameProjectCounts((next || source).get(counts.projectId), counts)) {
            if (!next) {
                next = new Map<number, ProjectConversationCounts>(source);
            }
            next.set(counts.projectId, counts);
        }
    }
    return next || source;
}

/** The order state of one target: `sequence` (none: nothing applied) with that target's `AsOfUtc` mark. */
function orderFor(sequence: CountsSequenceState | null | undefined, lastAsOfMs: number | null): CountsOrderState {
    return {
        lastAsOfMs,
        lastReadStateSeq: sequence ? sequence.lastReadStateSeq : 0,
        lastOptimisticSeq: sequence ? sequence.lastOptimisticSeq : 0,
        lastReadStateAppliedSeq: sequence ? sequence.lastReadStateAppliedSeq : 0,
    };
}

/** A project's `AsOfUtc` mark; null when none was applied to it. */
function projectMark(marks: CountsAsOfMarks | null | undefined, projectId: number): number | null {
    const mark = marks && marks.projectMs ? marks.projectMs.get(projectId) : null;

    return typeof mark === 'number' ? mark : null;
}

/** A count from an answer: a whole number ≥ 0; anything else is 0. */
function countOf(value: number | null | undefined): number {
    return typeof value === 'number' && isFinite(value) ? Math.max(0, Math.floor(value)) : 0;
}

/** A positive whole number. */
function isPositiveInteger(value: unknown): value is number {
    return typeof value === 'number' && Number.isInteger(value) && value > 0;
}

/** Compile-time exhaustiveness of `CountsAnswerKind`; at run time an unknown kind is never applied. */
function unknownAnswerKind(_kind: never): CountsAnswerVerdict {
    return 'stale';
}

/** Compile-time exhaustiveness of `CountsAnswerVerdict`; at run time an unknown verdict reconciles nothing. */
function unknownVerdict(_verdict: never): boolean {
    return false;
}
